/**
 * „SEPA-Sperre verwalten"-Drawer: zeigt alle aktiven Sperren einer Person (nach Vertrag
 * gruppiert), erlaubt Notiz + Bankverbindung/Mandat zu pflegen, gibt Sperren vertragsbezogen
 * frei und macht die Sperr-Historie sichtbar. Gemeinsamer Einstieg aus Stornierungen-Liste,
 * SEPA-Sperrliste und Personenakte — ein fachlicher Pfad statt Navigations-Hopping.
 */
import { App, Alert, Button, Collapse, Form, Input, Space, Tag, Typography } from "../../../../../shared/frontend/ui";
import { useQuery } from "@tanstack/react-query";
import { LoadingOverlay } from "./LoadingOverlay";
import dayjs from "dayjs";
import React from "react";
import { useAuth } from "../app/auth/useAuth";
import { spacing } from "../app/design-system";
import { useI18n } from "../app/i18n/useI18n";
import { useDateInputFormats } from "../app/units/DateInputFormatContext";
import { formatDateOnly } from "../app/units/format";
import { loadUnits } from "../app/units/unitsStorage";
import { chargebackReasonLabel } from "../app/utils/chargebackReason";
import { FormDrawer } from "./FormDrawer";
import { useFormDirty } from "../../../../../shared/frontend/forms/useFormDirty";
import { SepaBankDetailsFields } from "./SepaBankDetailsFields";
import { useFeedback } from "../../../../../shared/frontend/feedback/useFeedback";

import { ISO_DATE_LENGTH } from "../app/utils/isoDate";

const DRAWER_WIDTH = 520;

type SepaBlockDto = {
  id: string;
  personId: string;
  personName: string;
  blockType: number; // 0 = Mandate, 1 = WaitForPayment
  contractId?: string | null;
  contractName?: string | null;
  openItemDescription?: string | null;
  reasonCode?: string | null;
  note?: string | null;
  blockedUntil?: string | null;
  status: number; // 0 = Active
  blockedAt: string;
  releasedAt?: string | null;
  releaseReason?: string | null;
  releasedByUserName?: string | null;
};

type PersonLite = {
  id: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  homeStudioId?: string | null;
  bankAccountHolder?: string | null;
  iban?: string | null;
  bic?: string | null;
  bankName?: string | null;
  mandateReference?: string | null;
  mandateSignedDate?: string | null;
  sepaBlockNote?: string | null;
};

export type SepaBlockManageDrawerProps = {
  open: boolean;
  onClose: () => void;
  personId: string | null;
  /** Auslösender Block (z. B. aus Stornierungen) — wird hervorgehoben. */
  focusBlockId?: string | null;
  /** Nach Freigabe/Speichern (Listen und Personenakte neu laden). */
  onChanged?: () => void;
};

const BLOCK_TYPE_MANDATE = 0;
const BLOCK_STATUS_ACTIVE = 0;
const BLOCK_STATUS_RELEASED_AUTOMATICALLY = 1;

export function SepaBlockManageDrawer({ open, onClose, personId, focusBlockId, onChanged }: SepaBlockManageDrawerProps) {
  const auth = useAuth();
  const { text } = useI18n();
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const dateInputFormats = useDateInputFormats();
  const units = React.useMemo(() => loadUnits(auth.info?.accountId), [auth.info?.accountId]);
  const [form] = Form.useForm();
  const { dirty, sync, resetDirty, markSaved } = useFormDirty(form);
  const [saving, setSaving] = React.useState(false);
  const [releasing, setReleasing] = React.useState(false);
  const canWrite = auth.hasAnyPermission("payments.write");

  // Alle drei Abrufe unter einem Schlüssel: der Drawer zeigt sie ohnehin nur gemeinsam an.
  const blockQuery = useQuery({
    queryKey: ["persons", personId ?? "", "sepa-blocks"],
    queryFn: async () => {
      const id = encodeURIComponent(personId ?? "");
      const [personDto, blockRows, studioRows] = await Promise.all([
        auth.api.get<PersonLite>(`/api/v1/persons/${id}`),
        auth.api.get<SepaBlockDto[]>(`/api/v1/finance/sepa-blocked?personId=${id}&includeReleased=true`),
        auth.api.get<Array<{ id: string; code?: string | null }>>("/api/v1/studios")
      ]);
      return { person: personDto ?? null, blocks: blockRows ?? [], studios: studioRows ?? [] };
    },
    enabled: open && !!personId
  });

  const person = blockQuery.data?.person ?? null;
  const blocks = React.useMemo(() => blockQuery.data?.blocks ?? [], [blockQuery.data]);
  const studios = React.useMemo(() => blockQuery.data?.studios ?? [], [blockQuery.data]);
  const loading = blockQuery.isFetching;
  const load = React.useCallback(async () => {
    await blockQuery.refetch();
  }, [blockQuery]);

  // Das Formular ist imperativ; die geladenen Werte müssen weiterhin hineingeschrieben werden.
  React.useEffect(() => {
    if (!open || !personId) {
      form.resetFields();
      resetDirty();
      return;
    }
    if (!person) return;
    sync({
      bankAccountHolder: person.bankAccountHolder ?? null,
      iban: person.iban ?? null,
      bic: person.bic ?? null,
      bankName: person.bankName ?? null,
      mandateReference: person.mandateReference ?? null,
      mandateSignedDate: person.mandateSignedDate ? dayjs(String(person.mandateSignedDate).slice(0, ISO_DATE_LENGTH)) : null,
      sepaBlockNote: person.sepaBlockNote ?? null
    });
  }, [form, open, person, personId, sync, resetDirty]);

  const activeBlocks = blocks.filter((block) => block.status === BLOCK_STATUS_ACTIVE);
  const releasedBlocks = blocks.filter((block) => block.status !== BLOCK_STATUS_ACTIVE);
  const mandateBlocks = activeBlocks.filter((block) => block.blockType === BLOCK_TYPE_MANDATE);

  // Aktive Sperren nach Vertrag gruppieren; Mandatssperren bilden die personenweite Gruppe.
  const contractGroups = React.useMemo(() => {
    const groups = new Map<string, { contractId: string | null; contractName: string | null; blocks: SepaBlockDto[] }>();
    for (const block of activeBlocks) {
      if (block.blockType === BLOCK_TYPE_MANDATE) continue;
      const key = block.contractId || "";
      const group = groups.get(key) ?? { contractId: block.contractId ?? null, contractName: block.contractName ?? null, blocks: [] };
      group.blocks.push(block);
      groups.set(key, group);
    }
    return Array.from(groups.values());
  }, [activeBlocks]);

  const confirmRelease = (contractId: string | null, contractName: string | null) => {
    let comment = "";
    const affectsOtherContracts = mandateBlocks.length > 0 && contractGroups.some((group) => group.contractId !== contractId);
    modal.confirm({
      title: contractId ? text("Release blocks of this contract") : text("Unblock SEPA"),
      width: DRAWER_WIDTH,
      autoFocusButton: "cancel",
      okText: text("Unblock SEPA"),
      okButtonProps: { danger: true },
      cancelText: text("Cancel"),
      content: (
        <div style={{ marginTop: spacing.lg }}>
          {contractName ? <Typography.Paragraph strong>{contractName}</Typography.Paragraph> : null}
          {contractId && mandateBlocks.length > 0 ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: spacing.md }}
              message={affectsOtherContracts
                ? text("Releasing also lifts the person-wide mandate block affecting other contracts.")
                : text("Releasing also lifts the person-wide mandate block.")}
            />
          ) : null}
          <Typography.Paragraph type="secondary" style={{ marginBottom: spacing.xs }}>
            {text("Comment (optional, saved when unblocking SEPA)")}
          </Typography.Paragraph>
          <Input.TextArea rows={3} placeholder={text("Enter note...")} onChange={(event) => { comment = event.target.value; }} />
        </div>
      ),
      onOk: async () => {
        if (!personId) return;
        setReleasing(true);
        try {
          await auth.api.post("/api/v1/finance/sepa-blocked/release-for-contract", {
            personId,
            contractId,
            comment: comment.trim() || null
          });
          feedback.success(text("SEPA unblocked."));
          await load().catch(() => {});
          onChanged?.();
        } finally {
          setReleasing(false);
        }
      }
    });
  };

  const save = async () => {
    if (!personId) return;
    const values = await form.validateFields();
    setSaving(true);
    try {
      await auth.api.put(`/api/v1/persons/${encodeURIComponent(personId)}/bank-details`, {
        bankAccountHolder: values.bankAccountHolder || null,
        iban: values.iban || null,
        bic: values.bic || null,
        bankName: values.bankName || null,
        mandateReference: values.mandateReference || null,
        mandateSignedDate: values.mandateSignedDate?.isValid?.() ? values.mandateSignedDate.format("YYYY-MM-DD") : null
      });
      const note = String(values.sepaBlockNote || "").trim();
      if (activeBlocks.length > 0 && note !== String(person?.sepaBlockNote || "").trim()) {
        await auth.api.put(`/api/v1/persons/${encodeURIComponent(personId)}/sepa-block-note`, { text: note || null });
      }
      feedback.success(text("Saved."));
      markSaved(values);
      await load().catch(() => {});
      onChanged?.();
    } finally {
      setSaving(false);
    }
  };

  const renderBlock = (block: SepaBlockDto) => {
    const reason = chargebackReasonLabel(block.reasonCode) || block.reasonCode;
    return (
      <div
        key={block.id}
        style={{
          padding: spacing.sm,
          borderRadius: spacing.xs,
          border: focusBlockId === block.id ? "1px solid var(--ant-color-warning)" : "1px solid var(--ant-color-border)",
          marginBottom: spacing.sm
        }}
      >
        <Space direction="vertical" size={2} style={{ width: "100%" }}>
          <Space wrap>
            <Tag color={block.status === BLOCK_STATUS_ACTIVE ? "red" : "green"}>
              {block.blockType === BLOCK_TYPE_MANDATE ? text("Mandate block") : text("Wait for payment")}
            </Tag>
            {block.status !== BLOCK_STATUS_ACTIVE ? (
              <Tag>
                {block.status === BLOCK_STATUS_RELEASED_AUTOMATICALLY ? text("Released automatically") : text("Released manually")}
              </Tag>
            ) : null}
            <Typography.Text type="secondary">
              {text("Blocked at")}: {formatDateOnly(block.blockedAt.slice(0, ISO_DATE_LENGTH), units)}
            </Typography.Text>
            {block.blockedUntil ? (
              <Typography.Text type="secondary">
                {text("Block until")}: {formatDateOnly(String(block.blockedUntil).slice(0, ISO_DATE_LENGTH), units)}
              </Typography.Text>
            ) : null}
          </Space>
          {reason ? (
            <Typography.Text type="secondary">{text("Trigger")}: {reason}</Typography.Text>
          ) : null}
          {block.openItemDescription ? (
            <Typography.Text type="secondary">{text("Dunned contribution")}: {block.openItemDescription}</Typography.Text>
          ) : null}
          {block.releasedAt ? (
            <Typography.Text type="secondary">
              {text("Released at")}: {formatDateOnly(String(block.releasedAt).slice(0, ISO_DATE_LENGTH), units)}
              {block.releasedByUserName ? ` · ${text("Released by")}: ${block.releasedByUserName}` : ""}
            </Typography.Text>
          ) : null}
          {block.note ? (
            <Typography.Text type="secondary">{text("SEPA block note")}: {block.note}</Typography.Text>
          ) : null}
        </Space>
      </div>
    );
  };

  return (
    <FormDrawer mode={(!canWrite) ? "view" : "edit"}
      dirty={dirty}
      title="Manage SEPA block"
      titleNode={person ? `${text("Manage SEPA block")} — ${person.firstName} ${person.lastName}`.trim() : undefined}
      open={open}
      onCancel={onClose}
      formId="sepa-block-manage-form"
      submitting={saving}
      submitText="Save"
      hideSubmit={!canWrite}
      autoFocusFirstField={false}
    >
      {loading ? (
        <LoadingOverlay visible scope="placeholder" />
      ) : (
        <Form id="sepa-block-manage-form" form={form} layout="vertical" requiredMark={false} onFinish={() => save().catch(() => {})}>
          {activeBlocks.length === 0 ? (
            <Alert type="success" showIcon message={text("No active SEPA blocks.")} style={{ marginBottom: spacing.lg }} />
          ) : (
            <>
              {mandateBlocks.length > 0 ? (
                <div style={{ marginBottom: spacing.lg }}>
                  <Typography.Paragraph strong style={{ marginBottom: spacing.sm }}>
                    {text("Mandate block")} — {text("All contracts")}
                  </Typography.Paragraph>
                  {mandateBlocks.map(renderBlock)}
                  {canWrite && contractGroups.length === 0 ? (
                    <Button danger loading={releasing} onClick={() => confirmRelease(null, null)}>
                      {text("Unblock SEPA")}
                    </Button>
                  ) : null}
                </div>
              ) : null}
              {contractGroups.map((group) => (
                <div key={group.contractId || "none"} style={{ marginBottom: spacing.lg }}>
                  <Typography.Paragraph strong style={{ marginBottom: spacing.sm }}>
                    {group.contractName || text("Contract")}
                  </Typography.Paragraph>
                  {group.blocks.map(renderBlock)}
                  {canWrite ? (
                    <Button danger loading={releasing} onClick={() => confirmRelease(group.contractId, group.contractName)}>
                      {text("Release blocks of this contract")}
                    </Button>
                  ) : null}
                </div>
              ))}
              <Form.Item name="sepaBlockNote" label={text("SEPA block note")}>
                <Input.TextArea rows={2} placeholder={text("Enter note...")} />
              </Form.Item>
            </>
          )}

          <Typography.Paragraph strong style={{ marginBottom: spacing.sm }}>
            {text("Bank details")}
          </Typography.Paragraph>
          {mandateBlocks.length > 0 || !person?.mandateReference ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: spacing.md }}
              message={text("After a mandate block the mandate is voided — capture the new bank details and mandate here.")}
            />
          ) : null}
          <SepaBankDetailsFields
            api={auth.api}
            autoGenerateMandateOnIban={false}
            dateInputFormat={dateInputFormats}
            firstName={person?.firstName}
            form={form}
            homeStudioId={person?.homeStudioId}
            initialIban={person?.iban}
            lastName={person?.lastName}
            modal={modal}
            personId={personId}
            showTitle={false}
            studios={studios}
            text={text}
          />

          {releasedBlocks.length > 0 ? (
            <Collapse
              style={{ marginTop: spacing.lg }}
              items={[
                {
                  key: "history",
                  label: text("Block history"),
                  children: <>{releasedBlocks.map(renderBlock)}</>
                }
              ]}
            />
          ) : null}
        </Form>
      )}
    </FormDrawer>
  );
}
